import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Sparkles, Heart, Star } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1659522761084-79196b64abe4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const COLL = "https://images.pexels.com/photos/17763917/pexels-photo-17763917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";
const MKT = "https://images.unsplash.com/photo-1604005050685-97d0e19a1842?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function Row({ title, subtitle, products, to }) {
  if (!products.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">{subtitle}</p>
          <h2 className="font-display text-3xl md:text-4xl mt-2">{title}</h2>
        </div>
        <Link to={to} className="hidden sm:flex items-center gap-2 text-sm hover:text-[#D4AF37] transition-colors">View all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
        {products.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>
    </section>
  );
}

export default function Home() {
  const [best, setBest] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [deals, setDeals] = useState([]);
  const [cats, setCats] = useState([]);
  const [email, setEmail] = useState("");

  useEffect(() => {
    api.get("/products?tag=best_sellers&limit=4").then((r) => setBest(r.data.items));
    api.get("/products?tag=new&limit=4").then((r) => setArrivals(r.data.items));
    api.get("/products?tag=sale&limit=4").then((r) => setDeals(r.data.items));
    api.get("/products/categories").then((r) => setCats(r.data));
  }, []);

  const subscribe = async (e) => { e.preventDefault(); await api.post("/newsletter", { email }); toast.success("Welcome to GULIT — 10% off code: WELCOME10"); setEmail(""); };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[88vh] min-h-[560px] overflow-hidden">
        <img src={HERO} alt="GULIT" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-6 h-full flex flex-col justify-center">
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-sm animate-fade-up">The Marketplace, Reimagined</p>
          <h1 className="font-display text-5xl md:text-7xl font-bold text-white max-w-2xl mt-4 leading-[1.05] animate-fade-up" style={{ animationDelay: "100ms" }}>
            Trusted goods, crafted with warmth.
          </h1>
          <p className="text-white/85 max-w-md mt-6 text-lg animate-fade-up" style={{ animationDelay: "200ms" }}>
            From apparel to home, faith to gifting — discover pieces that carry the soul of the Gulit marketplace.
          </p>
          <div className="flex gap-4 mt-8 animate-fade-up" style={{ animationDelay: "300ms" }}>
            <Link to="/shop"><Button data-testid="hero-shop-btn" className="rounded-none bg-[#D4AF37] text-black font-semibold px-8 h-12 hover:brightness-110">Shop the Collection</Button></Link>
            <Link to="/shop?tag=new"><Button variant="outline" className="rounded-none border-white text-white bg-transparent h-12 px-8 hover:bg-white hover:text-black">New Arrivals</Button></Link>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-border">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {[[Truck, "Free Shipping", "On orders over $75"], [ShieldCheck, "Secure Checkout", "Stripe protected"], [Sparkles, "AI Stylist", "Smart recommendations"], [Heart, "Loved Worldwide", "10k+ happy customers"]].map(([Icon, t, s], i) => (
            <div key={i} className="flex items-center gap-3 p-6"><Icon className="h-6 w-6 text-[#D4AF37]" /><div><p className="font-medium text-sm">{t}</p><p className="text-xs text-muted-foreground">{s}</p></div></div>
          ))}
        </div>
      </section>

      {/* Featured collections */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] text-center">Curated For You</p>
        <h2 className="font-display text-3xl md:text-4xl mt-2 text-center mb-10">Featured Collections</h2>
        <div className="grid gap-5 md:grid-cols-12">
          {cats.slice(0, 5).map((c, i) => (
            <Link key={c.name} to={`/shop?category=${encodeURIComponent(c.name)}`} data-testid={`collection-${c.name.toLowerCase().replace(/\s/g,'-')}`}
              className={`group relative overflow-hidden bg-[#F5F1E8] dark:bg-[#141414] ${i === 0 ? "md:col-span-7 aspect-[16/10]" : i === 1 ? "md:col-span-5 aspect-[16/10]" : "md:col-span-4 aspect-square"}`}>
              <img src={c.image} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 p-6"><h3 className="font-display text-2xl text-white">{c.name}</h3><p className="text-white/80 text-sm">{c.count} items →</p></div>
            </Link>
          ))}
        </div>
      </section>

      <Row title="Best Sellers" subtitle="Loved Most" products={best} to="/shop?tag=best_sellers" />

      {/* Marketplace story banner */}
      <section className="relative my-8">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 items-center gap-10 bg-[#F5F1E8] dark:bg-[#141414]">
          <div className="py-16 lg:pr-10">
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">Our Story</p>
            <h2 className="font-display text-3xl md:text-4xl mt-3">Born from the Gulit marketplace</h2>
            <p className="text-muted-foreground mt-5 leading-relaxed">In Ethiopia, the <em>gulit</em> is where neighbors gather — to trade, to talk, to trust. We carry that spirit to your doorstep with thoughtfully sourced goods and a promise of warmth in every order.</p>
            <Link to="/about"><Button variant="outline" className="rounded-none mt-7 border-black dark:border-white">Discover GULIT</Button></Link>
          </div>
          <img src={MKT} alt="Marketplace" loading="lazy" className="h-full max-h-[460px] w-full object-cover" />
        </div>
      </section>

      <Row title="New Arrivals" subtitle="Just In" products={arrivals} to="/shop?tag=new" />

      {/* Limited deals */}
      {deals.length > 0 && (
        <section className="bg-black text-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="flex items-end justify-between mb-10">
              <div><p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">Limited-Time</p><h2 className="font-display text-3xl md:text-4xl mt-2">Deals of the Season</h2></div>
              <Link to="/shop?tag=sale" className="hidden sm:flex items-center gap-2 text-sm text-[#D4AF37]">Shop sale <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">{deals.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] text-center">Kind Words</p>
        <h2 className="font-display text-3xl md:text-4xl mt-2 text-center mb-12">What Our Community Says</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {[["The quality is exceptional and the packaging felt like a true gift. GULIT has my heart.", "Amira K."],
            ["Finally a store that blends faith, home and style so beautifully. Fast shipping too!", "Daniel R."],
            ["You can feel the care in every detail. The candles smell divine and last forever.", "Sofia L."]].map(([q, a], i) => (
            <div key={i} className="border border-border p-8">
              <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />)}</div>
              <p className="font-accent text-xl leading-relaxed">"{q}"</p>
              <p className="mt-4 text-sm tracking-wide uppercase text-muted-foreground">— {a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[#F5F1E8] dark:bg-[#141414]">
        <div className="mx-auto max-w-2xl px-6 py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl">Join the GULIT Circle</h2>
          <p className="text-muted-foreground mt-3">Get 10% off your first order, early access to drops, and stories from the marketplace.</p>
          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input data-testid="newsletter-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="rounded-none h-12 bg-background" />
            <Button data-testid="newsletter-submit" className="rounded-none h-12 px-8 bg-[#D4AF37] text-black font-semibold hover:brightness-110">Subscribe</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
